// client/src/utils/confirm.js
// Imperative Promise-based confirm() — replaces native window.confirm().
// Matches the app's design tokens from styles/global.css (--surface, --gold,
// --danger, --border, --radius). RTL/LTR derived from <html dir>. Hebrew /
// English labels from i18next, with prop overrides.
//
// Usage:
//   import { confirm } from '../../utils/confirm';
//
//   if (!(await confirm({ body: 'Delete this row?' }))) return;
//
//   const ok = await confirm({
//     title: 'מחיקת לקוח',
//     body: 'פעולה זו אינה הפיכה',
//     confirmLabel: 'מחק',
//     cancelLabel: 'ביטול',
//     danger: true,
//   });
//
// Created 2026-05-27 as L5 audit fix — replaces 57 native window.confirm() calls.

import React, { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import i18n from 'i18next';

const isHebrew = () => (i18n?.language || '').toLowerCase().startsWith('he');

function ConfirmModal({ title, body, confirmLabel, cancelLabel, danger, onConfirm, onCancel }) {
  const confirmRef = useRef(null);

  useEffect(() => {
    // Focus confirm button when modal opens
    confirmRef.current?.focus();
    // ESC = cancel, Enter = confirm
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      else if (e.key === 'Enter' && document.activeElement === confirmRef.current) {
        e.preventDefault(); onConfirm();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onConfirm, onCancel]);

  const he = isHebrew();
  const dir = he ? 'rtl' : 'ltr';
  const defaultConfirm = he ? 'אישור' : 'OK';
  const defaultCancel = he ? 'ביטול' : 'Cancel';
  const defaultTitle = he ? 'אישור פעולה' : 'Confirm';

  // Inline styles so we don't depend on a CSS file. Tokens mirror styles/global.css.
  const backdrop = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 10000, animation: 'confirmFadeIn 120ms ease-out',
  };
  const card = {
    background: '#ffffff', borderRadius: 8, padding: '24px 28px 20px',
    minWidth: 320, maxWidth: 520, boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
    direction: dir, fontFamily: "'DM Sans', sans-serif",
    animation: 'confirmSlideIn 160ms cubic-bezier(.2,.9,.3,1)',
  };
  const titleStyle = { fontSize: 17, fontWeight: 700, color: '#1a1d23', marginBottom: 10 };
  const bodyStyle  = { fontSize: 14.5, color: '#5c6370', lineHeight: 1.55, marginBottom: 22, whiteSpace: 'pre-wrap' };
  const actions    = { display: 'flex', justifyContent: 'flex-end', gap: 10, flexDirection: he ? 'row-reverse' : 'row' };
  const btnBase    = { padding: '8px 18px', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', border: '1px solid transparent', fontFamily: 'inherit' };
  const cancelBtn  = { ...btnBase, background: '#ffffff', color: '#5c6370', border: '1px solid #e3e6ea' };
  const confirmBtn = { ...btnBase, background: danger ? '#c0392b' : '#2563eb', color: '#ffffff' };

  return (
    <div style={backdrop} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <style>{`
        @keyframes confirmFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes confirmSlideIn { from { transform: translateY(-8px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
        .confirm-btn-confirm:hover { filter: brightness(0.93); }
        .confirm-btn-cancel:hover { background: #f8f9fa; }
      `}</style>
      <div style={card} role="dialog" aria-modal="true" aria-label={title || defaultTitle}>
        {title !== false && <div style={titleStyle}>{title || defaultTitle}</div>}
        <div style={bodyStyle}>{body || ''}</div>
        <div style={actions}>
          <button className="confirm-btn-cancel" style={cancelBtn} onClick={onCancel}>
            {cancelLabel || defaultCancel}
          </button>
          <button ref={confirmRef} className="confirm-btn-confirm" style={confirmBtn} onClick={onConfirm}>
            {confirmLabel || defaultConfirm}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Show a confirm modal. Returns a Promise that resolves to true (confirmed) or false (cancelled).
 * All fields optional. `danger: true` makes the confirm button red.
 */
export function confirm(opts = {}) {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    container.setAttribute('data-confirm-modal', '');
    document.body.appendChild(container);
    const root = createRoot(container);

    let settled = false;
    const cleanup = (result) => {
      if (settled) return;
      settled = true;
      // Defer unmount so we don't unmount inside a render
      setTimeout(() => {
        try { root.unmount(); } catch { /* already unmounted */ }
        container.remove();
      }, 0);
      resolve(result);
    };

    root.render(
      <ConfirmModal
        {...opts}
        onConfirm={() => cleanup(true)}
        onCancel={() => cleanup(false)}
      />
    );
  });
}
